/**
 * Aktivitätenhistorie of an Eingang across all its tasks. A sent reply is clickable and
 * opens the full mail in place. Built on the shared Timeline and EingangMailView.
 */

import { useState } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';
import type { PosteingangActivityEntry, PosteingangSentMail } from '@brokly/shared';
import { dateTimeShortDe, weekdayDateTimeDe } from '../../../lib/format';
import { Timeline, type TimelineEvent } from '../../entity-detail/components/Timeline';
import { EingangMailView } from './EingangMailView';
import { useEingangSentMail } from '../hooks/useEingangActivity';

/** "· N Anhänge" ("· 1 Anhang") for a sent reply's attachment count; empty when there are none. */
export function anhaengeSuffix(count: number): string {
  return count > 0 ? ` · ${count} ${count === 1 ? 'Anhang' : 'Anhänge'}` : '';
}

/** "Amelie hat eine E-Mail an x gesendet"; without a name (older rows) the passive form. */
export function sentLabel(entry: PosteingangActivityEntry): string {
  const to = entry.sent?.to?.trim() ? entry.sent.to : 'den Kunden';
  return entry.actor_name?.trim()
    ? `${entry.actor_name} hat eine E-Mail an ${to} gesendet`
    : `Eine E-Mail an ${to} wurde gesendet`;
}

/** The sent mail in the shape EingangMailView renders. */
function asMailView(mail: PosteingangSentMail) {
  return {
    id: mail.id,
    channel: 'email',
    direction: 'outbound',
    subject: mail.subject,
    from: mail.from,
    to: mail.to,
    cc: mail.cc,
    message_id: null,
    received_at: mail.sent_at,
    body: mail.body,
    body_html: mail.body_html,
  };
}

function SentMailView({
  commId,
  sentId,
  onBack,
}: {
  commId: string;
  sentId: string;
  onBack: () => void;
}) {
  const { data: mail, isLoading, isError } = useEingangSentMail(commId, sentId);
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="flex w-fit items-center gap-1.5 rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-accent"
      >
        <ArrowLeft className="size-3.5" />
        Zurück zur Historie
      </button>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Lade E-Mail …</p>
      ) : isError || !mail ? (
        <p className="text-sm text-muted-foreground">Diese E-Mail ist nicht verfügbar.</p>
      ) : (
        <article aria-label="Gesendete E-Mail" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">{mail.subject ?? '(kein Betreff)'}</h3>
          <p className="text-xs text-muted-foreground">
            Gesendet von {mail.sent_by_name ?? '-'} · {weekdayDateTimeDe(mail.sent_at)}
          </p>
          <EingangMailView comm={asMailView(mail)} tier={mail.tier} />
        </article>
      )}
    </div>
  );
}

export function AktivitaetenHistorie({
  commId,
  entries,
  isLoading = false,
}: {
  commId: string;
  entries: PosteingangActivityEntry[];
  isLoading?: boolean;
}) {
  const [openSentId, setOpenSentId] = useState<string | null>(null);

  if (openSentId) {
    return <SentMailView commId={commId} sentId={openSentId} onBack={() => setOpenSentId(null)} />;
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">Lade Historie …</p>;

  const events: TimelineEvent[] = entries.map((entry) => {
    const sent = entry.sent;
    // "· N Anhänge" so the broker sees whether the reply carried attachments (Jevin #37 review).
    // Omitted when there are none; the attachment itself is opened via the erledigt tab / the mail.
    const anhaenge = sent ? anhaengeSuffix(sent.attachment_count) : '';
    return sent
      ? {
          id: entry.id,
          text: (
            <span className="inline-flex items-start gap-1.5">
              <Mail className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <span>{sentLabel(entry)}</span>
            </span>
          ),
          datum: `${weekdayDateTimeDe(entry.created_at)}${sent.subject ? ` · ${sent.subject}` : ''}${anhaenge}`,
          onClick: () => setOpenSentId(sent.communication_id),
        }
      : {
          id: entry.id,
          text: (
            <>
              {/* whitespace-pre-line: the Systemanalyse entry (P2 hardening 05) carries newline-separated
                  Befund lines; without it the "\n" would collapse to a single run-on line. */}
              <span className="whitespace-pre-line">{entry.content ?? entry.type}</span>
              {entry.actor_name ? <span className="text-muted-foreground"> · {entry.actor_name}</span> : null}
            </>
          ),
          datum: dateTimeShortDe(entry.created_at),
        };
  });

  return <Timeline events={events} empty="Noch keine Einträge." />;
}
